import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import storiesData from './stories_data.json';
import rootsData from './roots_data.json';
import RootPage from './RootPage';
import { rootToSlug } from './utils/rootSlug';

const GENERIC_WAZN_RE = /[فعل]/;
function isGenericWazn(w) {
  // فَعَلَ / يَفْعُلُ etc. are pattern placeholders, not actual verb forms
  const bare = w.replace(/[\u064B-\u0652\u0670]/g, '');
  return GENERIC_WAZN_RE.test(bare);
}

// Look up basic verb forms from roots_data by root string
function getVerbForms(root) {
  if (!root) return null;
  const slug = rootToSlug(root);
  const entry = rootsData[slug];
  if (!entry || !entry.babs || !entry.babs.length) return null;
  const bab = entry.babs[0];
  // sarf_kabir may have a maloom/majhool nesting (old format) or be flat (new format)
  const sk = bab.sarf_kabir?.maloom || bab.sarf_kabir || {};

  // Use actual conjugated هُوَ form if wazn is a generic فعل placeholder
  const findHuwa = (rows) =>
    (rows || []).find(r => r.person === 'هُوَ' && r.gender === 'm' && r.num === 'sg')?.form || null;

  const past = !isGenericWazn(bab.wazn_madi || '')
    ? bab.wazn_madi
    : findHuwa(sk.madi?.rows);
  const present = !isGenericWazn(bab.wazn_mudari || '')
    ? bab.wazn_mudari
    : findHuwa(sk.mudari_marfu?.rows);

  const amrRows = sk.amr?.rows || [];
  const command = amrRows.find(r => r.person === 'أَنْتَ' && r.num === 'sg' && r.gender === 'm')?.form || null;

  return {
    past: past || null,
    present: present || null,
    masdar: bab.masdar || null,
    command,
  };
}

// ─── Palette ────────────────────────────────────────────────────────────────
// Green palette as vivid accents on clean neutral backgrounds
export const C = {
  // brand greens
  forest: '#315E49',
  moss:   '#264A39',
  sage:   '#5F7E6C',
  soft:   '#7F9A87',
  // alert
  orange: '#D4721A',
  // neutral backgrounds (modern, not warm)
  pageBgLight:  '#f8f8f8',
  pageBgDark:   '#0a0a0a',
  cardBgLight:  '#ffffff',
  cardBgDark:   '#111111',
  card2BgDark:  '#161616',
  borderLight:  '#ebebeb',
  borderDark:   '#1e1e1e',
  // text
  textLight:    '#0d0d0d',
  textDark:     '#f0f0f0',
  mutedLight:   '#888888',
  mutedDark:    '#666666',
};

const TYPE_STYLES = {
  verb:     { color: C.forest,  label: 'verb',     bg: 'rgba(49,94,73,0.08)'  },
  noun:     { color: C.sage,    label: 'noun',     bg: 'rgba(95,126,108,0.08)'},
  particle: { color: C.orange,  label: 'particle', bg: 'rgba(212,114,26,0.08)'},
  pronoun:  { color: '#9B6A4A', label: 'pronoun',  bg: 'rgba(155,106,74,0.08)'},
};

function buildSections(data) {
  const sections = {};
  data.forEach((item) => {
    const folder = item.folder;
    const sub = item.subfolder || '__root__';
    if (!sections[folder]) sections[folder] = {};
    if (!sections[folder][sub]) sections[folder][sub] = [];
    sections[folder][sub].push(item);
  });
  return sections;
}

// ─── Word Card ─────────────────────────────────────────────────────────────
const WordCard = ({ word, isSelected, onClick, hideDiacritics, hideEnglish, darkMode }) => {
  const arabic = hideDiacritics ? word.base : word.harakat;
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center rounded-xl px-2 py-1.5 sm:px-3 sm:py-2 transition-all duration-150 cursor-pointer select-none focus:outline-none"
      style={isSelected ? {
        outline: `2px solid ${C.forest}`,
        outlineOffset: '1px',
        backgroundColor: 'rgba(49,94,73,0.1)',
      } : {
        backgroundColor: 'transparent',
      }}
    >
      <span
        className="font-arabic leading-snug font-semibold"
        style={{
          fontSize: '1.2rem',
          color: darkMode ? C.textDark : C.textLight,
        }}
      >
        {arabic}
      </span>
      {/* Always reserve space so layout doesn't shift when translations toggled */}
      <span
        className="text-xs mt-0.5 font-normal"
        style={{
          color: darkMode ? C.mutedDark : C.mutedLight,
          visibility: hideEnglish ? 'hidden' : 'visible',
        }}
      >
        {word.english}
      </span>
    </button>
  );
};

// ─── Bottom Sheet ──────────────────────────────────────────────────────────
const SHEET_NORMAL = 45;   // vh
const SHEET_EXPANDED = 85; // vh

const BottomSheet = ({ word, darkMode, hideDiacritics, onClose }) => {
  const navigate = useNavigate();
  const isOpen = !!word;
  // Always show harakat in the info pane regardless of display setting
  const arabic = word ? word.harakat : '';
  const [expanded, setExpanded] = useState(false);
  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const dragStartY = useRef(null);

  // reset expansion when sheet closes
  useEffect(() => { if (!isOpen) setExpanded(false); }, [isOpen]);

  const handleDragStart = (clientY) => {
    dragStartY.current = clientY;
    setDragging(true);
  };
  const handleDragMove = (clientY) => {
    if (!dragging || dragStartY.current === null) return;
    // allow drag in both directions; clamp upward drag
    const delta = clientY - dragStartY.current;
    if (expanded) setDragY(Math.max(0, delta));        // expanded: only drag down
    else setDragY(delta);                               // normal: drag up or down
  };
  const handleDragEnd = () => {
    const delta = dragY;
    if (expanded) {
      if (delta > 80) setExpanded(false);              // drag down → shrink
    } else {
      if (delta < -60) setExpanded(true);              // drag up → expand
      else if (delta > 80) onClose();                  // drag down → close
    }
    setDragY(0);
    setDragging(false);
    dragStartY.current = null;
  };

  const maxHeight = expanded ? SHEET_EXPANDED : SHEET_NORMAL;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 rounded-t-3xl"
      style={{
        backgroundColor: darkMode ? C.cardBgDark : C.cardBgLight,
        borderTop: `1px solid ${darkMode ? C.borderDark : C.borderLight}`,
        transform: isOpen ? `translateY(${Math.max(0, dragY)}px)` : 'translateY(100%)',
        transition: dragging ? 'none' : 'transform 0.3s cubic-bezier(0.32,0.72,0,1), max-height 0.3s cubic-bezier(0.32,0.72,0,1)',
        maxHeight: `${maxHeight}vh`,
        overflowY: dragging ? 'hidden' : 'auto',
        boxShadow: '0 -12px 40px rgba(0,0,0,0.15)',
      }}
    >
      {/* Handle — drag target */}
      <div
        className="flex justify-center pt-3 pb-2 cursor-grab active:cursor-grabbing select-none"
        onMouseDown={e => handleDragStart(e.clientY)}
        onMouseMove={e => { if (dragging) handleDragMove(e.clientY); }}
        onMouseUp={handleDragEnd}
        onMouseLeave={handleDragEnd}
        onTouchStart={e => handleDragStart(e.touches[0].clientY)}
        onTouchMove={e => handleDragMove(e.touches[0].clientY)}
        onTouchEnd={handleDragEnd}
      >
        <div className="w-9 h-1 rounded-full" style={{ background: `linear-gradient(90deg, ${C.forest}, ${C.soft})` }} />
      </div>

      {word && (
        <>
          {/* Sheet header */}
          <div
            className="px-5 pt-3 pb-4 flex items-start justify-between gap-4"
            style={{ borderBottom: `1px solid ${darkMode ? C.borderDark : C.borderLight}` }}
          >
            <button
              onClick={onClose}
              className="mt-1 p-1.5 rounded-lg focus:outline-none transition-colors"
              style={{ color: darkMode ? C.mutedDark : C.mutedLight }}
            >
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
            <div className="flex flex-col items-center flex-1 gap-1">
              <span className="font-arabic text-4xl font-bold leading-tight" dir="rtl">
                {word.parts
                  ? word.parts.map((p, i) => (
                      <span key={i} style={{ color: TYPE_STYLES[p.pos]?.color || C.forest }}>{p.harakat}</span>
                    ))
                  : <span style={{ color: TYPE_STYLES[word.pos]?.color || (darkMode ? C.textDark : C.textLight) }}>{arabic}</span>
                }
              </span>
              <span className="text-sm" style={{ color: darkMode ? C.mutedDark : C.mutedLight }}>
                {word.english}
              </span>
            </div>
            <div className="w-8" />
          </div>

          {/* Word detail cards */}
          <div className="p-4 flex flex-col gap-3 pb-8">
            {(word.parts || [word]).map((part, i) => {
              const ts = TYPE_STYLES[part.pos] || TYPE_STYLES.verb;
              const verbForms = part.pos === 'verb' ? getVerbForms(part.root) : null;
              return (
                <div
                  key={i}
                  className="rounded-2xl overflow-hidden"
                  style={{
                    border: `1px solid ${darkMode ? C.borderDark : C.borderLight}`,
                    borderLeft: `3px solid ${ts.color}`,
                  }}
                >
                  <div className="flex items-center gap-2.5 px-4 py-2.5" style={{ backgroundColor: ts.bg }}>
                    <span className="font-arabic text-lg font-bold" style={{ color: ts.color }}>
                      {hideDiacritics ? part.base : part.harakat}
                    </span>
                    <span
                      className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full"
                      style={{ color: ts.color, border: `1px solid ${ts.color}40` }}
                    >
                      {ts.label}
                    </span>
                  </div>
                  <div className="px-4 py-3" style={{ backgroundColor: darkMode ? C.card2BgDark : '#fafafa' }}>
                    <p
                      className="text-sm leading-relaxed"
                      style={{
                        color: darkMode ? '#c0c0c0' : '#444',
                        marginBottom: (part.root || verbForms) ? '0.75rem' : 0,
                      }}
                    >
                      {part.english}
                    </p>
                    {(part.root || verbForms) && (
                      <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                        {part.root && (
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs" style={{ color: darkMode ? C.mutedDark : C.mutedLight }}>Root</span>
                            <button
                              onClick={() => {
                                const qp = new URLSearchParams();
                                if (part.harakat) qp.set('form', part.harakat);
                                navigate(`/root/${rootToSlug(part.root)}?${qp.toString()}`);
                                onClose();
                              }}
                              className="flex items-center gap-1.5 font-arabic font-semibold text-sm px-2.5 py-1 rounded-lg focus:outline-none active:scale-95 transition-transform"
                              style={{
                                color: C.forest,
                                backgroundColor: 'rgba(49,94,73,0.1)',
                                border: `1px solid rgba(49,94,73,0.25)`,
                              }}
                              dir="rtl"
                            >
                              {part.root}
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ transform: 'rotate(180deg)', flexShrink: 0 }}>
                                <path d="M5 12h14M12 5l7 7-7 7"/>
                              </svg>
                            </button>
                          </div>
                        )}
                        {verbForms && [
                          ['Past',         verbForms.past],
                          ['Present',      verbForms.present],
                          ['Verbal noun',  verbForms.masdar],
                          ['Command',      verbForms.command],
                        ].map(([label, val]) => val && (
                          <div key={label} className="flex items-center justify-between gap-2">
                            <span className="text-xs" style={{ color: darkMode ? C.mutedDark : C.mutedLight }}>{label}</span>
                            <span dir="rtl" className="font-arabic font-semibold text-sm" style={{ color: C.forest }}>{val}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

// ─── Sidebar ───────────────────────────────────────────────────────────────
const FOLDER_COLORS = [C.forest, C.orange, C.sage, '#9B6A4A', C.soft];

const Sidebar = ({ sections, selectedItem, setSelectedItem, isOpen, darkMode, onClose }) => (
  <>
    {isOpen && (
      <div className="fixed inset-0 z-30 bg-black/40 backdrop-blur-sm" onClick={onClose} />
    )}
    <aside
      className="fixed top-0 right-0 h-full z-40 w-72 flex flex-col shadow-2xl"
      style={{
        backgroundColor: darkMode ? C.cardBgDark : C.cardBgLight,
        borderLeft: `1px solid ${darkMode ? C.borderDark : C.borderLight}`,
        transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.3s ease',
      }}
    >
      <div
        className="flex items-center justify-between px-5 py-4"
        style={{ borderBottom: `1px solid ${darkMode ? C.borderDark : C.borderLight}` }}
      >
        <div className="flex items-center gap-2.5">
          <div className="w-1 h-5 rounded-full" style={{ background: `linear-gradient(180deg, ${C.forest}, ${C.soft})` }} />
          <h2 className="font-arabic text-lg font-bold" style={{ color: C.forest }}>الدليل</h2>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-lg focus:outline-none" style={{ color: darkMode ? C.mutedDark : C.mutedLight }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-3">
        {Object.entries(sections).map(([folder, subs], fi) => {
          const fc = FOLDER_COLORS[fi % FOLDER_COLORS.length];
          return (
            <div key={folder} className="mb-5">
              <div className="flex items-center gap-1.5 mb-2 px-2">
                <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: fc }} />
                <p className="text-[11px] font-semibold uppercase tracking-widest font-arabic" style={{ color: fc }}>{folder}</p>
              </div>
              {Object.entries(subs).map(([sub, items]) => (
                <div key={sub} className="mb-1">
                  {sub !== '__root__' && (
                    <p className="text-xs font-medium px-2 py-1.5 mb-1 font-arabic" style={{ color: darkMode ? C.mutedDark : C.mutedLight }}>
                      {sub.trim()}
                    </p>
                  )}
                  {items.map((item) => {
                    const title = item.stories?.[0]?.title || item.path;
                    const isActive = selectedItem?.path === item.path;
                    return (
                      <button
                        key={item.path}
                        onClick={() => { setSelectedItem(item); onClose(); }}
                        className="w-full text-right px-3 py-2 rounded-lg text-sm font-arabic mb-0.5 transition-all focus:outline-none"
                        style={isActive
                          ? { backgroundColor: C.forest, color: '#fff' }
                          : { color: darkMode ? '#aaa' : '#444' }
                        }
                      >
                        {title}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </aside>
  </>
);

// ─── Settings FAB ──────────────────────────────────────────────────────────
const SettingsFAB = ({ darkMode, setDarkMode, hideDiacritics, setHideDiacritics, hideEnglish, setHideEnglish }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const Toggle = ({ label, value, onChange, color }) => (
    <label className="flex items-center justify-between gap-4 cursor-pointer">
      <span className="text-sm" style={{ color: darkMode ? '#ccc' : '#444' }}>{label}</span>
      <button
        role="switch" aria-checked={value} onClick={() => onChange(!value)} dir="ltr"
        className="w-10 h-6 rounded-full relative focus:outline-none flex-shrink-0"
        style={{ backgroundColor: value ? color : (darkMode ? '#252525' : '#d4d4d4') }}
      >
        <span className="absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all duration-200"
          style={{ left: value ? '1.25rem' : '0.25rem' }} />
      </button>
    </label>
  );

  return (
    <div ref={ref} className="fixed bottom-6 right-4 z-40 flex flex-col items-end gap-3">
      {/* Drop-up panel */}
      {open && (
        <div
          className="rounded-2xl p-4 flex flex-col gap-4"
          style={{
            backgroundColor: darkMode ? C.cardBgDark : C.cardBgLight,
            border: `1px solid ${darkMode ? C.borderDark : C.borderLight}`,
            boxShadow: darkMode ? '0 -8px 32px rgba(0,0,0,0.6)' : '0 -8px 32px rgba(0,0,0,0.12)',
            minWidth: '210px',
          }}
        >
          <p className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: darkMode ? C.mutedDark : C.mutedLight }}>Display</p>
          <Toggle label="Dark mode"         value={darkMode}       onChange={setDarkMode}       color={C.forest} />
          <div style={{ borderTop: `1px solid ${darkMode ? C.borderDark : C.borderLight}` }} />
          <Toggle label="Hide diacritics"   value={hideDiacritics} onChange={setHideDiacritics} color={C.orange} />
          <Toggle label="Hide translations" value={hideEnglish}    onChange={setHideEnglish}    color={C.sage} />
        </div>
      )}
      {/* FAB button */}
      <button
        onClick={() => setOpen(v => !v)}
        className="w-12 h-12 rounded-full flex items-center justify-center shadow-lg focus:outline-none transition-all duration-200"
        style={{
          backgroundColor: open ? C.forest : (darkMode ? '#1e1e1e' : '#fff'),
          color: open ? '#fff' : (darkMode ? C.mutedDark : C.mutedLight),
          border: `1px solid ${darkMode ? C.borderDark : C.borderLight}`,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        }}
      >
        <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="3"/>
          <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
        </svg>
      </button>
    </div>
  );
};

// ─── Main App ──────────────────────────────────────────────────────────────
function MainApp({ darkModeOverride, setDarkModeOverride }) {
  const sections = buildSections(storiesData);
  const firstItem = storiesData[0] || null;
  const location = useLocation();
  const navigate = useNavigate();

  const [selectedItem, setSelectedItem] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const storyPath = params.get('story');
    if (storyPath) {
      const found = storiesData.find(item => item.path === storyPath);
      if (found) return found;
    }
    const savedPath = sessionStorage.getItem('fikr_selectedPath');
    if (savedPath) {
      const found = storiesData.find(item => item.path === savedPath);
      if (found) return found;
    }
    return firstItem;
  });
  const [selectedWord, setSelectedWord] = useState(null);
  const [activeWord, setActiveWord] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const darkMode = darkModeOverride;
  const setDarkMode = setDarkModeOverride;
  const [hideDiacritics, setHideDiacritics] = useState(false);
  const [hideEnglish, setHideEnglish] = useState(false);
  const stories = selectedItem?.stories || [];

  const selectItem = (item) => {
    setSelectedItem(item);
    const params = new URLSearchParams();
    params.set('story', item.path);
    navigate(`/?${params.toString()}`, { replace: false });
  };

  // Sync when user navigates back/forward
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const storyPath = params.get('story');
    if (storyPath) {
      const found = storiesData.find(item => item.path === storyPath);
      if (found && found.path !== selectedItem?.path) setSelectedItem(found);
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSelectedWord(null);
    setActiveWord(null);
    if (selectedItem?.path) sessionStorage.setItem('fikr_selectedPath', selectedItem.path);
  }, [selectedItem]);

  const handleWordClick = (si, li, wi) => {
    const isSame = selectedWord?.si === si && selectedWord?.li === li && selectedWord?.wi === wi;
    if (isSame) { setSelectedWord(null); setActiveWord(null); }
    else { setSelectedWord({ si, li, wi }); setActiveWord(stories[si]?.lines[li]?.[wi] || null); }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: darkMode ? C.pageBgDark : C.pageBgLight, color: darkMode ? C.textDark : C.textLight }}>
      {/* Header */}
      <header
        className="fixed top-0 left-0 right-0 z-20 h-16 flex items-center justify-between px-5 backdrop-blur-xl"
        style={{
          backgroundColor: darkMode ? 'rgba(10,10,10,0.9)' : 'rgba(255,255,255,0.9)',
          borderBottom: `1px solid ${darkMode ? C.borderDark : C.borderLight}`,
        }}
      >
        <div className="w-9" />

        <h1
          className="font-arabic text-xl font-bold"
          style={{ color: C.forest }}
        >
          مترجم لطلاب العلم
        </h1>

        <button
          onClick={() => setSidebarOpen(v => !v)}
          className="p-2 rounded-xl focus:outline-none transition-colors"
          style={{ color: darkMode ? C.mutedDark : C.mutedLight }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="6" x2="21" y2="6"/>
            <line x1="3" y1="12" x2="21" y2="12"/>
            <line x1="3" y1="18" x2="21" y2="18"/>
          </svg>
        </button>
      </header>

      <Sidebar
        sections={sections} selectedItem={selectedItem} setSelectedItem={selectItem}
        isOpen={sidebarOpen} darkMode={darkMode} onClose={() => setSidebarOpen(false)}
      />

      <main className="pt-20 pb-24 px-3 sm:px-8 max-w-5xl mx-auto">
        {stories.map((story, si) => (
          <section key={si} className="mb-8 sm:mb-16">
            <div className="flex items-center gap-3 mb-4 sm:mb-8 justify-center">
              <div className="h-px flex-1 max-w-16 rounded-full" style={{ background: `linear-gradient(90deg, transparent, ${C.orange}60)` }} />
              <h2 className="font-arabic text-xl sm:text-3xl font-bold" style={{ color: C.forest }}>
                {story.title}
              </h2>
              <div className="h-px flex-1 max-w-16 rounded-full" style={{ background: `linear-gradient(90deg, ${C.orange}60, transparent)` }} />
            </div>

            <div className="space-y-0">
              {story.lines.map((line, li) => {
                const isSelectedLine = selectedWord?.si === si && selectedWord?.li === li;
                return (
                  <div
                    key={li}
                    dir="rtl"
                    className="flex flex-wrap justify-end gap-0.5 sm:gap-2 py-1.5 sm:py-3 px-1 sm:px-2 rounded-xl transition-colors"
                    style={isSelectedLine
                      ? { backgroundColor: 'rgba(49,94,73,0.07)' }
                      : {}
                    }
                  >
                    {line.map((word, wi) => (
                      <WordCard
                        key={wi} word={word}
                        isSelected={selectedWord?.si === si && selectedWord?.li === li && selectedWord?.wi === wi}
                        onClick={() => handleWordClick(si, li, wi)}
                        hideDiacritics={hideDiacritics} hideEnglish={hideEnglish} darkMode={darkMode}
                      />
                    ))}
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </main>

      <SettingsFAB
        darkMode={darkMode} setDarkMode={setDarkMode}
        hideDiacritics={hideDiacritics} setHideDiacritics={setHideDiacritics}
        hideEnglish={hideEnglish} setHideEnglish={setHideEnglish}
      />

      <BottomSheet
        word={activeWord} darkMode={darkMode} hideDiacritics={hideDiacritics}
        onClose={() => { setSelectedWord(null); setActiveWord(null); }}
      />
    </div>
  );
}

export default function App() {
  const [darkMode, setDarkMode] = useState(false);
  useEffect(() => { document.documentElement.classList.toggle('dark', darkMode); }, [darkMode]);
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<MainApp darkModeOverride={darkMode} setDarkModeOverride={setDarkMode} />} />
        <Route path="/root/:slug" element={<RootPage darkMode={darkMode} />} />
      </Routes>
    </BrowserRouter>
  );
}
